import styled from "styled-components"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { getAspectRatioFromImage, getCssSizeValue, getMapKey } from "@/utils/utils"
import Image from "@/components/common/Image"
import Text from "@/components/common/Text"
import { StyledTileIndicator } from "@/components/TilesModule/variants/WhoHelpsTilesModule/index"
import { WHO_HELPS_VARIANTS } from "@/constants"
import { getImageSource } from "@/components/TeamModule/TeamMember"
import useGlobals from "@/hooks/useGlobals"

const StyledWrapper = styled.div`
	--aspect-ratio: 540/356;
	--cols-count: 5;
	min-height: ${getCssSizeValue(356)};

	display: grid;
	grid-template-columns: repeat(var(--cols-count), 1fr);
	column-gap: var(--grid-gap);
	position: relative;

	border: 1px solid ${(props) => props.theme.colors.richBlack};
	margin-bottom: ${getCssSizeValue(60, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-bottom: ${getCssSizeValue(48, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: unset;
	}

	&.variant-${WHO_HELPS_VARIANTS.DEFAULT} {
	}

	&.variant-${WHO_HELPS_VARIANTS.TWO_TEAM_MEMBERS} {
		--aspect-ratio: 765/356;
		--cols-count: 7;
	}

	&.variant-${WHO_HELPS_VARIANTS.MANY} {
		--aspect-ratio: 1329/356;
		--cols-count: 12;
	}
`

const ImageWrapper = styled.div`
	width: ${getCssSizeValue(100, BREAKPOINTS.MOBILE)};
	overflow: hidden;

	&.default {
		display: flex;
		align-items: center;
		justify-content: center;
		background: ${({ theme }) => theme.colors.whiteSmoke};
	}

	&.${WHO_HELPS_VARIANTS.MANY} {
		grid-column: span 2;
		width: unset;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		width: ${getCssSizeValue(202, BREAKPOINTS.DESKTOP)};
	}
`

const StyledImage = styled(Image)`
	aspect-ratio: 204/356;
	width: auto;
	height: 100%;

	&.default {
		height: ${getCssSizeValue(150)};
		aspect-ratio: 1;
	}
`

const LogoAndMembersDataWrapper = styled.div`
	grid-column: span 4;
	display: flex;
	flex-direction: column;

	&.variant-${WHO_HELPS_VARIANTS.MANY} {
		grid-column: span 5;
	}
`

const StyledIcon = styled(Image)`
	aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "16/9"};
	width: auto;
	height: ${getCssSizeValue(100, BREAKPOINTS.MOBILE)};
	margin-top: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		height: ${getCssSizeValue(100, BREAKPOINTS.TABLET)};
		margin-top: ${getCssSizeValue(16, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
		height: ${getCssSizeValue(100)};
		margin-top: ${getCssSizeValue(32)};
		margin-bottom: ${getCssSizeValue(40)};
	}
`

const MembersData = styled.div`
	display: grid;
	grid-template-rows: repeat(2, 1fr);
	grid-auto-flow: column;
	row-gap: ${getCssSizeValue(28)};
`

const NameAndRole = styled.div`
	display: flex;
	flex-direction: column;
`

export default function TeamMembersCard({ data, teamMemberTitle, variant, memberIcon }) {
	const maleDefaultImage = useGlobals((state) => state.teamMemberMaleDefaultImage)
	const femaleDefaultImage = useGlobals((state) => state.teamMemberFemaleDefaultImage)
	const images = data?.images || []
	const restInfo = data?.restInfo || []
	const iconAspectRatio = getAspectRatioFromImage(memberIcon)

	return (
		<StyledWrapper className={`variant-${variant}`}>
			<StyledTileIndicator>
				<Text className="square static">{teamMemberTitle}</Text>
			</StyledTileIndicator>
			{images.map((item, index) => {
				const { image, defaultImage } = getImageSource(
					restInfo[index]?.gender || "male",
					item,
					maleDefaultImage,
					femaleDefaultImage,
				)

				return (
					<ImageWrapper key={getMapKey(item, index)} className={`${defaultImage ? "default" : ""} ${variant}`}>
						<StyledImage
							className={defaultImage ? "default" : ""}
							src={image.url}
							alt={image.alt}
							focalPoint={image.focalPoint}
						/>
					</ImageWrapper>
				)
			})}
			<LogoAndMembersDataWrapper className={`variant-${variant}`}>
				<div>
					<StyledIcon
						src={memberIcon?.url || ""}
						alt={memberIcon?.alt || ""}
						focalPoint={memberIcon.focalPoint}
						$aspectRatio={iconAspectRatio}
					/>
				</div>
				<MembersData>
					{restInfo.map((item, index) => {
						const nameAndSurname = item?.nameAndSurname || ""
						const role = item?.role || ""
						return (
							<NameAndRole key={getMapKey(item, index)}>
								<Text className="typo-strawford typo-strawford-60">{nameAndSurname}</Text>
								<Text className="typo-strawford typo-strawford-14">{role}</Text>
							</NameAndRole>
						)
					})}
				</MembersData>
			</LogoAndMembersDataWrapper>
		</StyledWrapper>
	)
}
